import { useCallback, useEffect, useState } from 'react';

/** Clé de stockage — doit rester identique à celle de `public/theme-init.js`. */
export const CLE_STOCKAGE = 'presta-dark-mode';

/**
 * Thème clair/sombre, mémorisé et appliqué sur `<html data-theme>`.
 *
 * L'attribut `data-theme` est la seule source de vérité : la feuille de style en
 * dérive (`@custom-variant dark` et les surcharges de `dark-mode.css`). La classe
 * `dark` est posée en plus, pour les quelques utilitaires `dark:` explicites.
 */
export function useDarkMode(): { sombre: boolean; basculer: () => void } {
  const [sombre, setSombre] = useState<boolean>(() => {
    if (typeof document === 'undefined') return false;
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });

  const appliquer = useCallback((valeur: boolean) => {
    document.documentElement.setAttribute('data-theme', valeur ? 'dark' : 'light');
    document.documentElement.classList.toggle('dark', valeur);
    try {
      localStorage.setItem(CLE_STOCKAGE, String(valeur));
    } catch {
      /* stockage indisponible (navigation privée stricte) : vaut pour la session */
    }
    setSombre(valeur);
  }, []);

  // Le choix fait dans un autre onglet se propage à celui-ci.
  useEffect(() => {
    const ecouter = (evenement: StorageEvent) => {
      if (evenement.key !== CLE_STOCKAGE) return;
      const valeur = evenement.newValue === 'true';
      document.documentElement.setAttribute('data-theme', valeur ? 'dark' : 'light');
      document.documentElement.classList.toggle('dark', valeur);
      setSombre(valeur);
    };
    globalThis.addEventListener('storage', ecouter);
    return () => globalThis.removeEventListener('storage', ecouter);
  }, []);

  const basculer = useCallback(() => appliquer(!sombre), [appliquer, sombre]);

  return { sombre, basculer };
}
